import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../css/PIPForm.css';
import Navbar from './Navbar';
import { getAllCourseCategory, getAllUsers, setPricing } from '../Services/Api';

const SetPricing = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { user: '', course: '', price: '', gst: 'without' },
  });

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState([]);
  const [courseData, setCourseData] = useState([]);

  const userDropdownRef = useRef(null);
  const courseDropdownRef = useRef(null);

  // ✅ Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setCourseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ Handle User Selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setValue('user', user.id, { shouldValidate: true });
    setUserDropdownOpen(false);
    setUserSearch(user.fullname);
  };

  // ✅ Handle Course Selection
  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setValue('course', course.id, { shouldValidate: true });
    setCourseDropdownOpen(false);
    setCourseSearch(course.category_name.toLowerCase());
  };

  // ✅ Fetch Data (Users + Courses)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await getAllUsers();
        const courseRes = await getAllCourseCategory();

        setUserData(userRes?.data || []);
        setCourseData(courseRes?.data || courseRes || []);
      } catch (err) {
        console.error(err);
        toast.error('Error fetching data');
      }
    };
    fetchData();
  }, []);

  // ✅ Form Submit Handler
  const onSubmit = async (data) => {
    if (!selectedUser || !selectedCourse) {
      toast.error('Please select both a user and a course.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: data.user,
        course_id: data.course,
        // price: data.price, // (commented out because your backend ignores price)
        // gst: data.gst,     // same here
      };

      // console.log('Submitting payload:', payload);
      const response = await setPricing(payload);

      if (response?.status === 'success') {
        toast.success(response.message || 'Pricing saved successfully!');
        resetForm();
      } else if (response?.status === 'error') {
        if (response.errors) {
          // Laravel 422 validation errors
          Object.values(response.errors).forEach((msgs) =>
            msgs.forEach((msg) => toast.error(msg))
          );
        } else {
          toast.error(response.message || 'Error saving pricing.');
        }
      } else {
        toast.error('Unexpected server response.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Server error while submitting pricing.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Reset form
  const resetForm = () => {
    reset();
    setSelectedUser(null);
    setSelectedCourse(null);
    setUserSearch('');
    setCourseSearch('');
  };

  return (
    <>
      <Navbar />
      <div className="pip-registration-main">
        <ToastContainer />
        <div className="pip-registration-container">
          <div className="pip-registration-header">
            <h1 className="pip-registration-title">Pricing Management</h1>
            <p className="pip-registration-subtitle">
              Manage and update pricing details for your users.
            </p>
          </div>

          <form className="pip-registration-body" onSubmit={handleSubmit(onSubmit)}>
            {/* Row 1: Username Selection + OCP ID */}
            <div className="pip-registration-row">
              <div
                className="pip-registration-group pip-registration-required"
                ref={userDropdownRef}
              >
                <label className="pip-registration-label">Select User</label>
                <div className="custom-dropdown-container">
                  <input
                    type="text"
                    placeholder="Search user..."
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setUserDropdownOpen(true);
                    }}
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="pip-registration-input"
                  />
                  {userDropdownOpen && (
                    <div className="custom-dropdown-options">
                      {userData
                        .filter((u) =>
                          u.fullname?.toLowerCase().includes(userSearch.toLowerCase())
                        )
                        .map((u) => (
                          <div
                            key={u.id}
                            className="custom-dropdown-option"
                            onClick={() => handleUserSelect(u)}
                          >
                            {u.fullname} ({u.email})
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                {errors.user && <p className="error-text">User selection is required</p>}
                <input type="hidden" {...register('user', { required: true })} />
              </div>

              <div className="pip-registration-group">
                <label className="pip-registration-label">OCP ID</label>
                <input
                  type="text"
                  value={selectedUser ? selectedUser.ocp_cer_id : ''}
                  readOnly
                  className="pip-registration-input"
                />
              </div>
            </div>

            {/* Row 2: Email + Mobile */}
            <div className="pip-registration-row">
              <div className="pip-registration-group">
                <label className="pip-registration-label">Email</label>
                <input
                  type="text"
                  value={selectedUser ? selectedUser.email : ''}
                  readOnly
                  className="pip-registration-input"
                />
              </div>
              <div className="pip-registration-group">
                <label className="pip-registration-label">Mobile</label>
                <input
                  type="text"
                  value={selectedUser ? selectedUser.phone : ''}
                  readOnly
                  className="pip-registration-input"
                />
              </div>
            </div>

            {/* Row 3: Course Selection */}
            <div className="pip-registration-row">
              <div
                className="pip-registration-group pip-registration-required"
                ref={courseDropdownRef}
              >
                <label className="pip-registration-label">Select Course</label>
                <div className="custom-dropdown-container">
                  <input
                    type="text"
                    placeholder="Search course..."
                    value={courseSearch}
                    onChange={(e) => {
                      setCourseSearch(e.target.value);
                      setCourseDropdownOpen(true);
                    }}
                    onClick={() => setCourseDropdownOpen(!courseDropdownOpen)}
                    className="pip-registration-input"
                  />
                  {courseDropdownOpen && (
                    <div
                      className="custom-dropdown-options"
                      style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                      }}
                    >
                      {courseData
                        .filter((c) =>
                          c.category_name
                            ?.toLowerCase()
                            .includes(courseSearch.toLowerCase())
                        )
                        .map((c) => (
                          <div
                            key={c.id}
                            className="custom-dropdown-option"
                            onClick={() => handleCourseSelect(c)}
                          >
                            {c.category_name.toLowerCase()}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                {errors.course && (
                  <p className="error-text">Course selection is required</p>
                )}
                <input type="hidden" {...register('course', { required: true })} />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pip-registration-footer">
              <button
                type="button"
                onClick={resetForm}
                className="pip-registration-btn pip-registration-btn-reset"
              >
                Reset Form
              </button>
              <button
                type="submit"
                className="pip-registration-btn pip-registration-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SetPricing;
